import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashLayout from "@/components/DashLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { CalendarDays, Save, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Nursery","LKG","UKG","1","2","3","4","5","6","7","8","9","10","11","12"];
const STATUS = ["present", "absent", "late", "excused"];

export default function SessionAttendance() {
  const { user } = useAuth();
  const isParent = user?.role === "parent" || user?.role === "student";
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({
    class_name: "5", section: "",
    subject_id: "", date: new Date().toISOString().slice(0, 10), period: 1,
  });
  const [entries, setEntries] = useState({});
  const [reasons, setReasons] = useState({});

  const load = async () => {
    try {
      const [s, st, h] = await Promise.all([
        api.get("/erp/subjects"),
        isParent ? Promise.resolve({ data: [] }) : api.get("/students"),
        api.get(`/erp/session-attendance${isParent ? "" : `?class_name=${form.class_name}`}`),
      ]);
      setSubjects(s.data); setStudents(st.data); setHistory(h.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { load(); }, [form.class_name]); // eslint-disable-line

  const classStudents = useMemo(() =>
    students.filter((s) => s.class_name === form.class_name && (!form.section || s.section === form.section)),
    [students, form.class_name, form.section]
  );

  const submit = async () => {
    if (!form.subject_id) { toast.error("Select subject"); return; }
    const missing = classStudents.filter((s) => entries[s.id] === "absent" && !(reasons[s.id] || "").trim());
    if (missing.length) {
      toast.error(`Reason required for absent students (${missing.length})`);
      return;
    }
    try {
      const payload = {
        class_name: form.class_name, section: form.section, subject_id: form.subject_id,
        date: form.date, period: Number(form.period) || 1,
        entries: classStudents.map((s) => ({
          student_id: s.id,
          status: entries[s.id] || "present",
          reason: reasons[s.id] || "",
        })),
      };
      await api.post("/erp/session-attendance", payload);
      toast.success("Session attendance saved · parents notified for absentees");
      setEntries({}); setReasons({}); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <DashLayout variant={user?.role === "teacher" ? "teacher" : "school"} title="Session-wise Attendance">
      {!isParent && (
        <Card className="p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div><Label className="text-xs">Class</Label>
              <Select value={form.class_name} onValueChange={(v) => setForm({ ...form, class_name: v })}>
                <SelectTrigger data-testid="sa-class"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Section</Label>
              <Input value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} data-testid="sa-section" />
            </div>
            <div><Label className="text-xs">Subject</Label>
              <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="sa-subject"><SelectValue placeholder="Select subject" /></SelectTrigger>
                <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="sa-date" />
            </div>
            <div><Label className="text-xs">Period</Label>
              <Input type="number" min="1" max="10" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="sa-period" />
            </div>
          </div>
        </Card>
      )}

      {!isParent && (
        <Card className="p-0 overflow-hidden mb-4">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead>
              <TableHead>Roll / Reg</TableHead>
              <TableHead className="w-40">Status</TableHead>
              <TableHead>Reason (mandatory on Absent)</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {classStudents.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">No students in this class</TableCell></TableRow>}
              {classStudents.map((s) => {
                const status = entries[s.id] || "present";
                return (
                  <TableRow key={s.id} data-testid={`sa-row-${s.id}`}>
                    <TableCell className="font-semibold">{s.first_name} {s.last_name}</TableCell>
                    <TableCell className="font-mono text-xs">{s.registration_no || s.roll_no || "—"}</TableCell>
                    <TableCell>
                      <Select value={status} onValueChange={(v) => setEntries({ ...entries, [s.id]: v })}>
                        <SelectTrigger data-testid={`sa-status-${s.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {(status === "absent" || status === "late") ? (
                        <Input
                          value={reasons[s.id] || ""}
                          onChange={(e) => setReasons({ ...reasons, [s.id]: e.target.value })}
                          placeholder={status === "absent" ? "Sick / Medical / Family reason…" : "Traffic / late…"}
                          className={status === "absent" && !(reasons[s.id] || "").trim() ? "border-red-400" : ""}
                          data-testid={`sa-reason-${s.id}`}
                        />
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {classStudents.length > 0 && (
            <div className="p-3 border-t bg-slate-50 flex justify-end">
              <Button onClick={submit} className="bg-emerald-700 hover:bg-emerald-800" data-testid="sa-submit"><Save className="w-4 h-4 mr-1" />Save Session Attendance</Button>
            </div>
          )}
        </Card>
      )}

      <Card className="p-4">
        <h3 className="font-bold mb-3">Recent Session History {isParent && "(your child)"}</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {history.length === 0 && <div className="text-slate-400 text-sm">No records yet</div>}
          {history.map((h) => (
            <div key={h.id} className="border rounded p-3 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{h.subject_name || "Subject"} · Period {h.period}</div>
                  <div className="text-xs text-slate-500">{h.date} · Class {h.class_name}{h.section ? ` ${h.section}` : ""} · Teacher: {h.teacher_name}</div>
                </div>
                <Badge variant="outline">{h.entries?.length || 0} students</Badge>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(h.entries || []).slice(0, 30).map((e, i) => (
                  <Badge key={i} variant="outline" className={
                    e.status === "present" ? "border-emerald-300 text-emerald-700" :
                    e.status === "absent" ? "border-red-300 text-red-700" :
                    e.status === "late" ? "border-amber-300 text-amber-700" : "border-slate-300 text-slate-600"
                  }>
                    {e.status}{e.reason ? ` · ${e.reason}` : ""}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </DashLayout>
  );
}
